import { Radio, RadioChangeEvent } from 'antd';
import Text from 'components/Text/Text';
import ErrorModals from './components/ErrorModals/ErrorModals';
import { v4 as uuidv4 } from 'uuid';
import CropperModal from './components/CropperModal';
import Uploader from './components/Uploader';
import PDFBlock from './components/PDFBlock';
import { TFileId, TLanguageIDs, ICustomGroups, TKeyForNewItemToCarousel, TMediaFileType } from 'types';
import IStyleSettingsCarouselUploaderState from './styleSettingsCarouselUploaderState/StyleSettingsCarouselUploaderState';
import React, { useEffect, useMemo, useState } from 'react';
import { aspectRatiosForStyleSettings } from 'appConstants';
import style from './StyleSettingsCarouselUploader.module.scss';

interface IStyleSettingsCarouselUploader {
	group: ICustomGroups;
	languageId: TLanguageIDs;
	selectedLanguage: null | TLanguageIDs;
	deleteHandler: (languageId: null | TLanguageIDs, groupId: string, translationId: string) => void;
	addItemHandler: (
		languageId: null | TLanguageIDs,
		groupId: string,
		translationId: string,
		itemKey: TKeyForNewItemToCarousel,
		mediaFileType: TMediaFileType,
		sourceUrl: string,
		fileId: TFileId,
	) => void;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	replacePdfToCarousel: ({
		translationIdOfItemForDeletion,
		languageId,
		groupId,
		translationId,
		itemKey,
		fileId,
	}: {
		translationIdOfItemForDeletion: string | undefined;
		languageId: TLanguageIDs;
		groupId: string;
		translationId: string;
		itemKey: TKeyForNewItemToCarousel;
		fileId: TFileId;
	}) => void;
	isDisabledControls?: boolean;
}

const StyleSettingsCarouselUploader: React.FC<IStyleSettingsCarouselUploader> = ({
	group,
	languageId,
	selectedLanguage,
	deleteHandler,
	addItemHandler,
	showGroupLoader,
	hideGroupLoader,
	replacePdfToCarousel,
	isDisabledControls,
}) => {
	const [actualPageOfCarousel, setActualPageOfCarousel] = useState<number>(0);
	const [localGroup, setLocalGroup] = useState<ICustomGroups>(group);
	const [localGroupOnlyMediaFiles, setLocalGroupOnlyMediaFiles] = useState<ICustomGroups>(group);
	const [imageOrientation, setImageOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
	const imageMaxFileSizeMB = 1;
	const videoMaxFileSizeMB = ['screen_saver_for_ordering', 'screen_saver_without_ordering'].includes(localGroup.key)
		? 5
		: 25;
	const pdfMaxFileSizeMB = 25;

	const hasMediaFile = useMemo(() => {
		return localGroup.items.some((el) => el.mediaFileType === 1 || el.mediaFileType === 2);
	}, [localGroup]);

	useEffect(() => {
		const actualItems = group.items.filter((el) => el.fileId !== null);
		setLocalGroup({
			id: localGroup.id,
			key: localGroup.key,
			isMultipleItems: localGroup.isMultipleItems,
			items: actualItems,
		});

		const actualItemsOnlyMediaFiles = group.items.filter(
			(el) => el.fileId !== null && (el.mediaFileType === 1 || el.mediaFileType === 2),
		);
		setLocalGroupOnlyMediaFiles({
			id: localGroup.id,
			key: localGroup.key,
			isMultipleItems: localGroup.isMultipleItems,
			items: actualItemsOnlyMediaFiles,
		});
	}, [group]);

	const handleDeleteItem = () => {
		const translationId = localGroupOnlyMediaFiles.items[actualPageOfCarousel].translationId;
		deleteHandler(languageId, localGroup.id, translationId);
	};

	const handleUploadItem = (mediaFileType: TMediaFileType, sourceUrl: string, fileId: string) => {
		const uniqueId = uuidv4();
		let itemKey: TKeyForNewItemToCarousel;
		switch (localGroup.key) {
			case 'how_it_works':
				itemKey = 'image_how_it_works';
				break;
			case 'free_right_button':
				itemKey = 'image_free_right_button';
				break;
			default:
				itemKey = 'image_screen_saver';
		}
		addItemHandler(languageId, localGroup.id, uniqueId, itemKey, mediaFileType, sourceUrl, fileId);
	};

	const handleChange = (e: RadioChangeEvent) => {
		setImageOrientation(e.target.value);
	};

	const handleUploadPdf = (fileId: string, keyForGroup: TKeyForNewItemToCarousel) => {
		// If there is already one .pdf file in the array of elements, it must be deleted and a new file added
		const translationIdOfItemForDeletion = localGroup.items.find(
			(el) => el.mediaFileType === 3 && el.fileId !== null,
		)?.translationId;
		const uniqueId = uuidv4();
		replacePdfToCarousel({
			translationIdOfItemForDeletion,
			languageId,
			groupId: localGroup.id,
			translationId: uniqueId,
			itemKey: keyForGroup,
			fileId,
		});
	};

	const handleDeletePdf = (keyForGroup: TKeyForNewItemToCarousel) => {
		// If there is already one .pdf file in the array of elements, it must be deleted and a new file added
		const translationIdOfItemForDeletion = localGroup.items.find(
			(el) => el.mediaFileType === 3 && el.fileId !== null,
		)?.translationId;
		replacePdfToCarousel({
			translationIdOfItemForDeletion,
			languageId,
			groupId: localGroup.id,
			translationId: translationIdOfItemForDeletion || '',
			itemKey: keyForGroup,
			fileId: null,
		});
	};

	return (
		<>
			<Radio.Group
				className={style.orientationSwitch}
				value={imageOrientation}
				disabled={isDisabledControls}
				onChange={handleChange}>
				<Radio value="horizontal" data-test="horizontalRatio">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="horizontalOrientationLabel"
						size="s"
					/>
				</Radio>
				<Radio value="vertical" data-test="verticalRatio">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="verticalOrientationLabel"
						size="s"
					/>
				</Radio>
			</Radio.Group>
			<IStyleSettingsCarouselUploaderState>
				<Uploader
					onSetActualPageOfCarousel={setActualPageOfCarousel}
					languageId={languageId}
					group={localGroup}
					selectedLanguage={selectedLanguage}
					selectedIndex={actualPageOfCarousel}
					hasMediaFile={hasMediaFile}
					videoMaxFileSizeMB={videoMaxFileSizeMB}
					deleteHandler={handleDeleteItem}
					addItemHandler={handleUploadItem}
					showGroupLoader={showGroupLoader}
					hideGroupLoader={hideGroupLoader}
					isDisabledControls={isDisabledControls}
				/>
				<CropperModal
					imageOrientation={imageOrientation}
					horizontalAspectRatio={aspectRatiosForStyleSettings[localGroup.key]?.horizontal}
					verticalAspectRatio={aspectRatiosForStyleSettings[localGroup.key]?.vertical}
					maxFileSizeMB={imageMaxFileSizeMB}
					showGroupLoader={showGroupLoader}
					hideGroupLoader={hideGroupLoader}
					onUploadItem={handleUploadItem}
				/>
				{localGroup.key === 'how_it_works' && (
					<PDFBlock
						onUploadPDF={handleUploadPdf}
						onDeleteFile={handleDeletePdf}
						keyForGroup="image_how_it_works"
						IdForPDFUploader={localGroup.id + languageId + 'pdf'}
						group={localGroup}
						isShowPDFUploader={!isDisabledControls}
						maxFileSizeMB={pdfMaxFileSizeMB}
					/>
				)}
				{localGroup.key === 'free_right_button' && (
					<PDFBlock
						onUploadPDF={handleUploadPdf}
						onDeleteFile={handleDeletePdf}
						keyForGroup="image_free_right_button"
						IdForPDFUploader={localGroup.id + languageId + 'pdf'}
						group={localGroup}
						isShowPDFUploader={!isDisabledControls}
						maxFileSizeMB={pdfMaxFileSizeMB}
					/>
				)}
				<ErrorModals
					imageMaxFileSizeMB={imageMaxFileSizeMB}
					videoMaxFileSizeMB={videoMaxFileSizeMB}
					pdfMaxFileSizeMB={pdfMaxFileSizeMB}
				/>
			</IStyleSettingsCarouselUploaderState>
		</>
	);
};

export default StyleSettingsCarouselUploader;
